import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, CheckCircle2, Ban, BarChart3, Search, Download, Eye, Pencil, Trash2, ChevronLeft, ChevronRight, Home, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { EmptyState } from "@/components/EmptyState";

import { confirmAction } from "@/lib/dialogs";
/* Units of Measure page layout. Presentation + the edit dialog; UnitsOfMeasure.jsx still owns loading, toggling, create and the drawer. */
const PAGE = 25;
const TONES = {
  blue:   { card: "from-[#EEF4FE] to-white border-[#DCE7F8] dark:from-[#1B2740] dark:to-[#202733] dark:border-[#2B3A57]", tile: "bg-[#DDE9FB] text-[#2563EB] dark:bg-[#243556] dark:text-[#8FB6F0]", bars: "text-[#2563EB] dark:text-[#8FB6F0]" },
  green:  { card: "from-[#EAF8F0] to-white border-[#D3EFDF] dark:from-[#17302A] dark:to-[#202733] dark:border-[#24463B]", tile: "bg-[#D5F1E2] text-[#16A34A] dark:bg-[#1F4034] dark:text-[#6FD1AA]", bars: "text-[#16A34A] dark:text-[#6FD1AA]" },
  red:    { card: "from-[#FDECEF] to-white border-[#F8D5DB] dark:from-[#3A1F29] dark:to-[#202733] dark:border-[#5A2C3A]", tile: "bg-[#FAD9DF] text-[#E11D48] dark:bg-[#4E2634] dark:text-[#F0899F]", bars: "text-[#E11D48] dark:text-[#F0899F]" },
  orange: { card: "from-[#FEF4E6] to-white border-[#F9E3C4] dark:from-[#3A3018] dark:to-[#202733] dark:border-[#554620]", tile: "bg-[#FCE6C7] text-[#F59E0B] dark:bg-[#4A3D1C] dark:text-[#E8BE5A]", bars: "text-[#F59E0B] dark:text-[#E8BE5A]" },
};

function StatTile({ tone, icon: Icon, label, value, loading }) {
  const t = TONES[tone];
  return (
    <div className={`flex items-center gap-4 rounded-2xl border bg-gradient-to-br p-4 shadow-sm ${t.card}`} data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-2xl ${t.tile}`}><Icon className="h-7 w-7" /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">{loading ? "–" : value}</p>
      </div>
      <BarChart3 className={`h-6 w-6 shrink-0 self-end ${t.bars}`} aria-hidden />
    </div>
  );
}

function exportCsv(rows, filename) {
  if (!rows.length) return;
  const heads = Object.keys(rows[0]);
  const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [heads.join(","), ...rows.map((r) => heads.map((h) => esc(r[h])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

/* The API allows changing the name only (the code is fixed once created). */
function EditUomDialog({ uom, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(!!uom), () => { setName(""); });      // closed without saving: opens fresh next time
  useEffect(() => { setName(uom?.name || ""); setErrors({}); }, [uom]);
  const submit = async (e) => {
    e.preventDefault(); setErrors({}); setSaving(true);
    try {
      await api.patch(`/masters/uom/${uom.id}`, { name: name.trim() });
      toast.success(`${uom.code} updated`); onClose(); onSaved?.();
    } catch (err) { setErrors(getFieldErrors(err)); toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!uom} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit Unit of Measure</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="edit-uom-form">
          <div><Label>Code</Label><Input value={uom?.code || ""} disabled className="mt-1.5" /><p className="mt-1 text-xs text-muted-foreground">The code can&apos;t be changed once created.</p></div>
          <div><Label>Name</Label><Input required data-testid="uom-edit-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" /><FieldError error={errors.name} /></div>
          <DialogFooter><Button type="submit" disabled={saving || !name.trim()} data-testid="edit-uom-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UomDesign({ uoms, shown, loading, search, onSearch, canManage, canDelete = canManage, onToggle, onView, onChanged, createAction }) {
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  useEffect(() => { setPage(1); }, [search]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const cur = Math.min(page, pages);
  const rows = useMemo(() => shown.slice((cur - 1) * PAGE, cur * PAGE), [shown, cur]);
  const from = shown.length ? (cur - 1) * PAGE + 1 : 0;
  const remove = async (u) => {
    if (!await confirmAction(`Delete unit ${u.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/masters/uom/${u.id}`);
      toast.success(`${u.code} deleted`);
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const iconBtn = "grid h-8 w-8 place-items-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground";

  return (
    <div className="space-y-6" data-testid="uom-page">
      <div>
        <nav className="mb-3 flex items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/" className="inline-flex items-center gap-1.5 hover:text-foreground"><Home className="h-3.5 w-3.5" />Home</Link>
          <ChevronRight className="h-3.5 w-3.5" /><span>Operations</span><ChevronRight className="h-3.5 w-3.5" /><span className="font-medium text-foreground">Units of Measure</span>
        </nav>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#E1F2F4] text-[#0F7C8C] dark:bg-[#17383D] dark:text-[#6CCBD6]"><Boxes className="h-7 w-7" /></span>
            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">Units of Measure</h1>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">Standard measurement units used across procurement, inventory and bills.</p>
            </div>
          </div>
          {createAction && <div className="shrink-0">{createAction}</div>}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="page-stat-cards">
        <StatTile tone="blue" icon={Boxes} label="Total UoMs" value={uoms.length} loading={loading} />
        <StatTile tone="green" icon={CheckCircle2} label="Active" value={uoms.filter((u) => u.is_active).length} loading={loading} />
        <StatTile tone="red" icon={Ban} label="Inactive" value={uoms.filter((u) => !u.is_active).length} loading={loading} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="table-toolbar">
          <div className="relative min-w-[220px] max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="toolbar-search-input" value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search..."
              className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[#910029] focus:ring-2 focus:ring-[#910029]/20" />
          </div>
          {shown.length > 0 && (
            <button type="button" data-testid="toolbar-export-button" className="inline-flex h-11 items-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-semibold text-foreground shadow-sm hover:bg-accent"
              onClick={() => exportCsv(shown.map((u) => ({ Code: u.code, Name: u.name, Status: u.is_active ? "Active" : "Inactive" })), "units-of-measure.csv")}>
              <Download className="h-4 w-4" />Export
            </button>
          )}
        </div>

        {!loading && uoms.length === 0 ? (
          <EmptyState title="No units of measure yet" testId="uom-empty-state" />
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  {["Code", "Name", "Status"].map((h) => <TableHead key={h} className="h-11 text-xs font-bold uppercase tracking-wide text-muted-foreground">{h}</TableHead>)}
                  <TableHead className="h-11 text-right text-xs font-bold uppercase tracking-wide text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((u, i) => (
                  <TableRow key={u.id} data-testid={`uom-row-${u.code}`} className={`h-[38px] ${i % 2 === 1 ? "bg-secondary/40" : ""}`}>
                    <TableCell className="py-1.5 text-sm text-foreground">{u.code}</TableCell>
                    <TableCell className="py-1.5 text-sm text-foreground">{u.name}</TableCell>
                    <TableCell className="py-1.5">
                      <div className="flex items-center gap-2.5">
                        <Switch checked={u.is_active} disabled={!canManage} onCheckedChange={() => onToggle(u)} className="h-5 w-10 data-[state=checked]:bg-[#910029]" data-testid={`uom-active-toggle-${u.code}`} />
                        <span className={`rounded-md px-2.5 py-0.5 text-xs font-medium ${u.is_active ? "bg-[#FBD5DD] text-[#910029] dark:bg-[#4E2634] dark:text-[#F0899F]" : "bg-secondary text-muted-foreground"}`}>{u.is_active ? "Active" : "Inactive"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-1.5 text-right">
                      <div className="flex items-center justify-end gap-2" data-testid={`uom-actions-${u.code}`}>
                        {canManage && <button type="button" title="Edit" onClick={() => setEditing(u)} data-testid={`uom-actions-${u.code}-edit`} className={iconBtn}><Pencil className="h-3.5 w-3.5" /></button>}
                        {canDelete && <button type="button" title="Delete" aria-label={`Delete ${u.code}`} onClick={() => remove(u)} data-testid={`uom-actions-${u.code}-delete`} className={iconBtn}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button>}
                        <button type="button" title="View" onClick={() => onView(u)} data-testid={`uom-actions-${u.code}-view`} className={iconBtn}><Eye className="h-3.5 w-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">No units match your search.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">Showing {from} to {from ? from + rows.length - 1 : 0} of {shown.length} units of measure</p>
          <div className="flex items-center gap-2">
            <button type="button" disabled={cur <= 1} onClick={() => setPage(cur - 1)} aria-label="Previous page" className="grid h-10 w-10 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" onClick={() => setPage(n)} aria-current={n === cur ? "page" : undefined}
                className={`grid h-10 w-10 place-items-center rounded-lg border text-sm font-semibold ${n === cur ? "border-[#910029] bg-[#FBE9EE] text-[#910029] dark:bg-[#4E2634] dark:text-[#F0899F]" : "border-border text-muted-foreground hover:bg-accent"}`}>{n}</button>
            ))}
            <button type="button" disabled={cur >= pages} onClick={() => setPage(cur + 1)} aria-label="Next page" className="grid h-10 w-10 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      <EditUomDialog uom={editing} onClose={() => setEditing(null)} onSaved={onChanged} />
    </div>
  );
}
